import {
  MatchStatusBadge,
  PaymentDetailSheet,
  PaymentInboxActions,
  PurchaseOrderSheet,
  SalesOrderSheet,
  makePendingKey,
  type MatchAction,
  type MatchStatus,
  type PaymentDetailRow,
  type PendingKey,
} from "@/components/operations";
import {
  formatCurrency,
  formatStatusLabel,
} from "@/components/operations/operations-shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  AirwallexPayment,
  GetOptions,
  MercuryPayment,
  PaypalPayment,
  PurchaseOrder,
  SalesOrder,
} from "@/core";
import {
  useAirwallexPayments,
  useAllOpenInvoices,
  useAllPurchaseOrders,
  useAllSalesOrders,
  useInboxExport,
  useMercuryPayments,
  usePaypalPayments,
  usePurchaseOrdersByIds,
  useResolveAirwallexPaymentMatch,
  useResolveMercuryPaymentMatch,
  useResolvePaypalPaymentMatch,
  useSalesOrdersByIds,
} from "@/hooks";
import {
  buildExportSuffix,
  endOfDay,
  formatDateRangeLabel,
  monthToDateRange,
  startOfDay,
  yearToDateRange,
} from "@/utils";
import {
  exportTransactionsToCsv,
  type TransactionExportRow,
} from "@/utils/csv-export";
import { formatTimestamp } from "@/utils/formatting";
import { getPurchaseOrderTotal } from "@/utils/order-total";
import {
  isPrivateMercuryCounterparty,
  isPrivatePaypalEmail,
} from "@/utils/private-paypal-emails";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarIcon,
  Download,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

const COLUMN_COUNT = 9;

type InboxSource = "paypal" | "mercury" | "airwallex";
type SourceFilter = "all" | InboxSource;
type DirectionFilter = "all" | "outgoing" | "incoming";
type StatusFilter = "all" | MatchStatus;

// A PayPal or Mercury inbox payment flattened into one shape so the combined
// table can render both. `source` decides which resolve mutation handles its
// match/reject/unmatch actions.
interface InboxRow {
  key: string; // `${source}:${id}`
  id: string;
  source: InboxSource;
  direction: "outgoing" | "incoming";
  // Outgoing payments reconcile against a purchase order; incoming Airwallex
  // credits reconcile against a sales order (via its invoice).
  targetKind: "po" | "so";
  paidAt?: Date;
  amount: number;
  feeValue: number;
  // The other party: who we paid (outgoing) or who paid us (incoming). Never a
  // bank memo or note — those live in `description`, which is not a substitute
  // for a payer we simply don't have.
  counterparty?: string;
  /** A second identifier for the same party (PayPal name behind its email). */
  counterpartySubtitle?: string;
  /** Our own account the money left from / landed in. */
  account?: string;
  /** Free-text bank memo / note, shown only in the detail sheet. */
  description?: string;
  /**
   * Lower-cased counterparty + bank description/memo text the keyword filter
   * matches against. Built per source because `description` keeps only one of
   * Mercury's note / memo / bank description, and the bank line (e.g. "AMEX
   * EPAYMENT; ACH PMT; …") is often what someone searches for.
   */
  searchText: string;
  title: string;
  currency?: string;
  transactionId?: string;
  matchStatus: MatchStatus;
  // PO target (outgoing).
  suggestedPoId?: string;
  matchedPoId?: string;
  // SO/invoice target (incoming Airwallex).
  suggestedSoId?: string;
  matchedSoId?: string;
  suggestedInvoiceId?: string;
  matchedInvoiceId?: string;
  suggestionMismatchPct?: number;
  detailRows: PaymentDetailRow[];
}

function buildSearchText(...parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(" ").toLowerCase();
}

// Firestore has no substring search, so the keyword filter runs client-side on
// the rows already fetched (and on the full set when exporting).
function matchesKeyword(row: InboxRow, keyword: string): boolean {
  const needle = keyword.trim().toLowerCase();
  return !needle || row.searchText.includes(needle);
}

const SOURCE_OPTIONS: { label: string; value: SourceFilter }[] = [
  { label: "All", value: "all" },
  { label: "PayPal", value: "paypal" },
  { label: "Mercury", value: "mercury" },
  { label: "Airwallex", value: "airwallex" },
];

const DIRECTION_OPTIONS: { label: string; value: DirectionFilter }[] = [
  { label: "All", value: "all" },
  { label: "Outgoing", value: "outgoing" },
  { label: "Incoming", value: "incoming" },
];

const TRANSACTIONS_PAGE_SIZE = 50;

const SOURCE_LABEL: Record<InboxSource, string> = {
  paypal: "PayPal",
  mercury: "Mercury",
  airwallex: "Airwallex",
};

const STATUS_OPTIONS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Unmatched", value: "unmatched" },
  { label: "Suggested", value: "suggested" },
  { label: "Matched", value: "matched" },
  { label: "Rejected", value: "rejected" },
];

function SourceBadge({ source }: { source: InboxSource }) {
  if (source === "paypal") {
    return (
      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
        PayPal
      </Badge>
    );
  }
  if (source === "mercury") {
    return (
      <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">
        Mercury
      </Badge>
    );
  }
  return (
    <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100">
      Airwallex
    </Badge>
  );
}

function DirectionBadge({ direction }: { direction: "outgoing" | "incoming" }) {
  if (direction === "incoming") {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        <ArrowDownLeft className="mr-1 h-3 w-3" />
        In
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
      <ArrowUpRight className="mr-1 h-3 w-3" />
      Out
    </Badge>
  );
}

function paypalToRow(p: PaypalPayment): InboxRow {
  return {
    key: `paypal:${p.id}`,
    id: p.id,
    source: "paypal",
    direction: p.direction ?? "outgoing",
    targetKind: "po",
    paidAt: p.paidAt,
    amount: p.amount,
    feeValue: p.feeValue,
    counterparty: p.counterpartyEmail || p.counterpartyName,
    // Only a subtitle when it adds something beyond the email above it.
    counterpartySubtitle: p.counterpartyEmail ? p.counterpartyName : undefined,
    // PayPal has no per-account concept — everything moves through the one
    // business account.
    account: "PayPal",
    description: p.note,
    searchText: buildSearchText(
      p.counterpartyEmail,
      p.counterpartyName,
      p.note,
    ),
    title: p.counterpartyEmail || p.counterpartyName || "PayPal payment",
    currency: p.currency,
    transactionId: p.transactionId,
    matchStatus: p.matchStatus,
    suggestedPoId: p.suggestedPoId,
    matchedPoId: p.matchedPoId,
    suggestionMismatchPct: p.suggestionMismatchPct,
    detailRows: [
      { label: "Source", value: "PayPal" },
      {
        label: "Direction",
        value: formatStatusLabel(p.direction ?? "outgoing"),
      },
      { label: "Email", value: p.counterpartyEmail },
      { label: "Name", value: p.counterpartyName },
      {
        label: "PayPal status",
        value: p.paypalStatus ? formatStatusLabel(p.paypalStatus) : undefined,
      },
      { label: "Event code", value: p.eventCode },
      { label: "Note", value: p.note },
      { label: "Invoice ID", value: p.invoiceId },
      { label: "Custom field", value: p.customField },
      { label: "Currency", value: p.currency },
      {
        label: "Paid at",
        value: formatTimestamp(p.paidAt, "yyyy-MM-dd HH:mm"),
      },
      { label: "Transaction ID", value: p.transactionId },
    ],
  };
}

// PayPal funding debits mirror a payout already in the PayPal inbox; private
// vendors are hidden like their PayPal payments.
function isVisibleMercuryPayment(m: MercuryPayment): boolean {
  return !m.paypalFunding && !isPrivateMercuryCounterparty(m.counterpartyId);
}

function mercuryToRow(m: MercuryPayment): InboxRow {
  const direction = m.direction ?? "outgoing";
  const isIncoming = direction === "incoming";
  return {
    key: `mercury:${m.id}`,
    id: m.id,
    source: "mercury",
    direction,
    targetKind: isIncoming ? "so" : "po",
    paidAt: m.paidAt,
    amount: m.amount,
    feeValue: m.feeValue,
    counterparty: m.counterpartyName,
    counterpartySubtitle:
      m.counterpartyNickname && m.counterpartyNickname !== m.counterpartyName
        ? m.counterpartyNickname
        : undefined,
    account: m.accountName,
    description: m.note || m.externalMemo || m.bankDescription,
    searchText: buildSearchText(
      m.counterpartyName,
      m.counterpartyNickname,
      m.bankDescription,
      m.externalMemo,
      m.note,
    ),
    title: m.counterpartyName || "Mercury payment",
    currency: m.currency,
    transactionId: m.transactionId,
    matchStatus: m.matchStatus,
    suggestedPoId: m.suggestedPoId,
    matchedPoId: m.matchedPoId,
    suggestedSoId: m.suggestedSoId,
    matchedSoId: m.matchedSoId,
    suggestedInvoiceId: m.suggestedInvoiceId,
    matchedInvoiceId: m.matchedInvoiceId,
    suggestionMismatchPct: m.suggestionMismatchPct,
    detailRows: [
      { label: "Source", value: "Mercury" },
      {
        label: "Direction",
        value: formatStatusLabel(m.direction ?? "outgoing"),
      },
      {
        label: isIncoming ? "Payer" : "Counterparty",
        value: m.counterpartyName,
      },
      { label: "Nickname", value: m.counterpartyNickname },
      { label: "Account", value: m.accountName },
      { label: "Type", value: m.kind ? formatStatusLabel(m.kind) : undefined },
      {
        label: "Mercury status",
        value: m.mercuryStatus ? formatStatusLabel(m.mercuryStatus) : undefined,
      },
      { label: "Note", value: m.note },
      { label: "External memo", value: m.externalMemo },
      { label: "Bank description", value: m.bankDescription },
      { label: "Currency", value: m.currency },
      {
        label: "Paid at",
        value: formatTimestamp(m.paidAt, "yyyy-MM-dd HH:mm"),
      },
      { label: "Counterparty ID", value: m.counterpartyId },
      { label: "Transaction ID", value: m.transactionId },
    ],
  };
}

function airwallexToRow(a: AirwallexPayment): InboxRow {
  const direction = a.direction ?? "outgoing";
  const isIncoming = direction === "incoming";

  // The sync used to store an incoming credit's bank memo as its counterparty,
  // and it preserves already matched/rejected records untouched — so legacy rows
  // still carry the memo in both fields. Identical values mean there is no real
  // payer, not that the payer happens to be the memo.
  const counterparty =
    isIncoming && a.counterpartyName === a.bankDescription
      ? undefined
      : a.counterpartyName;

  const detailRows: PaymentDetailRow[] = [
    { label: "Source", value: "Airwallex" },
    { label: "Direction", value: formatStatusLabel(direction) },
    // Payer (incoming) / beneficiary (outgoing) and the bank memo are separate
    // things — the memo is not a stand-in for a payer we don't have.
    {
      label: isIncoming ? "Payer" : "Counterparty",
      value: counterparty,
    },
    { label: "Description", value: a.bankDescription },
    { label: "Account", value: a.accountName },
    { label: "Type", value: a.kind ? formatStatusLabel(a.kind) : undefined },
    {
      label: "Airwallex status",
      value: a.airwallexStatus
        ? formatStatusLabel(a.airwallexStatus)
        : undefined,
    },
    { label: "Currency", value: a.currency },
    { label: "Paid at", value: formatTimestamp(a.paidAt, "yyyy-MM-dd HH:mm") },
    ...(isIncoming
      ? []
      : [{ label: "Beneficiary ID", value: a.counterpartyId }]),
    { label: "Transaction ID", value: a.transactionId },
  ];

  return {
    key: `airwallex:${a.id}`,
    id: a.id,
    source: "airwallex",
    direction,
    targetKind: isIncoming ? "so" : "po",
    paidAt: a.paidAt,
    amount: a.amount,
    feeValue: a.feeValue,
    counterparty,
    account: a.accountName,
    description: a.bankDescription,
    searchText: buildSearchText(counterparty, a.bankDescription),
    // The sheet header still falls back to the memo — a label is better than a
    // generic one, even though the memo is not the counterparty.
    title: counterparty || a.bankDescription || "Airwallex payment",
    currency: a.currency,
    transactionId: a.transactionId,
    matchStatus: a.matchStatus,
    suggestedPoId: a.suggestedPoId,
    matchedPoId: a.matchedPoId,
    suggestedSoId: a.suggestedSoId,
    matchedSoId: a.matchedSoId,
    suggestedInvoiceId: a.suggestedInvoiceId,
    matchedInvoiceId: a.matchedInvoiceId,
    suggestionMismatchPct: a.suggestionMismatchPct,
    detailRows,
  };
}

export default function PaymentsPage() {
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [directionFilter, setDirectionFilter] =
    useState<DirectionFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [keyword, setKeyword] = useState("");

  const [sheetPoId, setSheetPoId] = useState("");
  const [sheetSoId, setSheetSoId] = useState("");
  const [detailKey, setDetailKey] = useState<string | null>(null);

  const [matchDialogKey, setMatchDialogKey] = useState<string | null>(null);
  // Holds the selected target id for the manual match — a PO id for outgoing
  // rows, an invoice id for incoming (sales-order) rows.
  const [selectedIdForMatch, setSelectedIdForMatch] = useState("");
  const [matchSearchEnabled, setMatchSearchEnabled] = useState(false);

  const [pendingKeys, setPendingKeys] = useState<Set<PendingKey>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [exportSummary, setExportSummary] = useState<string | null>(null);

  // Status + date range are applied server-side (per collection); source,
  // direction and keyword are applied client-side on the merged rows.
  const rangeStart = useMemo(
    () => (dateRange?.from ? startOfDay(dateRange.from) : undefined),
    [dateRange],
  );
  const rangeEnd = useMemo(() => {
    const end = dateRange?.to || dateRange?.from;
    return end ? endOfDay(end) : undefined;
  }, [dateRange]);

  const queryOptions = useMemo(() => {
    const queryConstraints: Array<{
      field: string;
      operator: string;
      value: unknown;
    }> = [];
    if (statusFilter !== "all") {
      queryConstraints.push({
        field: "matchStatus",
        operator: "==",
        value: statusFilter,
      });
    }
    if (rangeStart) {
      queryConstraints.push({
        field: "paidAt",
        operator: ">=",
        value: rangeStart,
      });
    }
    if (rangeEnd) {
      queryConstraints.push({
        field: "paidAt",
        operator: "<=",
        value: rangeEnd,
      });
    }
    return {
      orderBy: { field: "paidAt", direction: "desc" } as const,
      pagination: { limit: TRANSACTIONS_PAGE_SIZE },
      ...(queryConstraints.length ? { queryConstraints } : {}),
    };
  }, [statusFilter, rangeStart, rangeEnd]);

  const paypalQuery = usePaypalPayments(
    queryOptions as GetOptions<PaypalPayment>,
  );
  const mercuryQuery = useMercuryPayments(
    queryOptions as GetOptions<MercuryPayment>,
  );
  const airwallexQuery = useAirwallexPayments(
    queryOptions as GetOptions<AirwallexPayment>,
  );

  const paypalRows = useMemo(
    () =>
      (paypalQuery.data?.pages.flat() ?? [])
        .filter(
          (p) =>
            Boolean(p.counterpartyEmail?.trim()) &&
            !isPrivatePaypalEmail(p.counterpartyEmail),
        )
        .map(paypalToRow),
    [paypalQuery.data],
  );

  const mercuryRows = useMemo(
    () =>
      (mercuryQuery.data?.pages.flat() ?? [])
        .filter(isVisibleMercuryPayment)
        .map(mercuryToRow),
    [mercuryQuery.data],
  );

  const airwallexRows = useMemo(
    () => (airwallexQuery.data?.pages.flat() ?? []).map(airwallexToRow),
    [airwallexQuery.data],
  );

  const rows = useMemo(() => {
    const merged: InboxRow[] = [];
    if (sourceFilter === "all" || sourceFilter === "paypal")
      merged.push(...paypalRows);
    if (sourceFilter === "all" || sourceFilter === "mercury")
      merged.push(...mercuryRows);
    if (sourceFilter === "all" || sourceFilter === "airwallex")
      merged.push(...airwallexRows);
    const filtered = merged.filter(
      (row) =>
        (directionFilter === "all" || row.direction === directionFilter) &&
        matchesKeyword(row, keyword),
    );
    filtered.sort(
      (a, b) => (b.paidAt?.getTime() ?? 0) - (a.paidAt?.getTime() ?? 0),
    );
    return filtered;
  }, [
    paypalRows,
    mercuryRows,
    airwallexRows,
    sourceFilter,
    directionFilter,
    keyword,
  ]);

  const statusCounts = useMemo(() => {
    const counts = { suggested: 0, unmatched: 0, matched: 0, rejected: 0 };
    for (const row of rows) counts[row.matchStatus]++;
    return counts;
  }, [rows]);

  const referencedPoIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of rows) {
      if (row.targetKind !== "po") continue;
      if (row.suggestedPoId) ids.push(row.suggestedPoId);
      if (row.matchedPoId) ids.push(row.matchedPoId);
    }
    return ids;
  }, [rows]);

  const referencedSoIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of rows) {
      if (row.targetKind !== "so") continue;
      if (row.suggestedSoId) ids.push(row.suggestedSoId);
      if (row.matchedSoId) ids.push(row.matchedSoId);
    }
    return ids;
  }, [rows]);

  const poMap = usePurchaseOrdersByIds(referencedPoIds);
  const soMap = useSalesOrdersByIds(referencedSoIds);

  // Match-picker sources. Outgoing rows pick a PO; incoming rows pick an open
  // invoice (labelled with its sales order's customer). Lazily enabled.
  const matchDialogRowForSources = matchDialogKey
    ? (rows.find((r) => r.key === matchDialogKey) ?? null)
    : null;
  const needPoSearch =
    matchSearchEnabled && matchDialogRowForSources?.targetKind !== "so";
  const needInvoiceSearch =
    matchSearchEnabled && matchDialogRowForSources?.targetKind === "so";

  const { purchaseOrders: allPurchaseOrders, isLoading: allPosLoading } =
    useAllPurchaseOrders(needPoSearch);
  const { invoices: allOpenInvoices, isLoading: allInvoicesLoading } =
    useAllOpenInvoices(needInvoiceSearch);
  const { salesOrders: allSalesOrders } = useAllSalesOrders(needInvoiceSearch);

  const paypalMutation = useResolvePaypalPaymentMatch();
  const mercuryMutation = useResolveMercuryPaymentMatch();
  const airwallexMutation = useResolveAirwallexPaymentMatch();

  const { fetchAll: fetchAllForExport, exporting } = useInboxExport();

  const showPaypal = sourceFilter === "all" || sourceFilter === "paypal";
  const showMercury = sourceFilter === "all" || sourceFilter === "mercury";
  const showAirwallex = sourceFilter === "all" || sourceFilter === "airwallex";
  const isLoading =
    (showPaypal && paypalQuery.isLoading) ||
    (showMercury && mercuryQuery.isLoading) ||
    (showAirwallex && airwallexQuery.isLoading);
  const hasNextPage =
    (showPaypal && paypalQuery.hasNextPage) ||
    (showMercury && mercuryQuery.hasNextPage) ||
    (showAirwallex && airwallexQuery.hasNextPage);
  const isFetchingNextPage =
    paypalQuery.isFetchingNextPage ||
    mercuryQuery.isFetchingNextPage ||
    airwallexQuery.isFetchingNextPage;

  // Surface query errors instead of silently rendering an empty inbox. A
  // Firestore missing-index error carries a direct index-creation URL in its
  // message, so we log the full error and show it in the UI.
  const queryErrors = useMemo(() => {
    const candidates: Array<[string, unknown]> = [
      ["PayPal", showPaypal ? paypalQuery.error : null],
      ["Mercury", showMercury ? mercuryQuery.error : null],
      ["Airwallex", showAirwallex ? airwallexQuery.error : null],
    ];
    const errors: Array<[string, Error]> = [];
    for (const [source, error] of candidates) {
      if (error instanceof Error) errors.push([source, error]);
    }
    return errors;
  }, [
    showPaypal,
    showMercury,
    showAirwallex,
    paypalQuery.error,
    mercuryQuery.error,
    airwallexQuery.error,
  ]);

  useEffect(() => {
    for (const [source, error] of queryErrors) {
      console.error(`[payments-inbox] ${source} query failed:`, error);
    }
  }, [queryErrors]);

  function loadMore() {
    if (showPaypal && paypalQuery.hasNextPage) paypalQuery.fetchNextPage();
    if (showMercury && mercuryQuery.hasNextPage) mercuryQuery.fetchNextPage();
    if (showAirwallex && airwallexQuery.hasNextPage)
      airwallexQuery.fetchNextPage();
  }

  // Exports the FULL set matching the current filters (not just loaded pages),
  // fetched directly rather than from the paginated inbox queries.
  async function handleExportCsv() {
    const { paypal, mercury, airwallex } = await fetchAllForExport({
      statusFilter,
      startDate: rangeStart,
      endDate: rangeEnd,
    });

    const merged: InboxRow[] = [];
    if (showPaypal) {
      merged.push(
        ...paypal
          .filter(
            (p) =>
              Boolean(p.counterpartyEmail?.trim()) &&
              !isPrivatePaypalEmail(p.counterpartyEmail),
          )
          .map(paypalToRow),
      );
    }
    if (showMercury) {
      merged.push(...mercury.filter(isVisibleMercuryPayment).map(mercuryToRow));
    }
    if (showAirwallex) merged.push(...airwallex.map(airwallexToRow));

    const filtered = merged
      .filter(
        (row) =>
          (directionFilter === "all" || row.direction === directionFilter) &&
          matchesKeyword(row, keyword),
      )
      .sort((a, b) => (b.paidAt?.getTime() ?? 0) - (a.paidAt?.getTime() ?? 0));

    const exportRows: TransactionExportRow[] = filtered.map((row) => ({
      source: SOURCE_LABEL[row.source],
      direction: row.direction,
      date: row.paidAt,
      amount: row.amount,
      fee: row.feeValue,
      currency: row.currency ?? "USD",
      counterparty: row.counterparty ?? "",
      account: row.account ?? "",
      description: row.description ?? "",
      status: row.matchStatus,
      order:
        row.matchedPoId ||
        row.matchedInvoiceId ||
        row.matchedSoId ||
        row.suggestedPoId ||
        row.suggestedInvoiceId ||
        row.suggestedSoId ||
        "",
      transactionId: row.transactionId ?? "",
    }));

    exportTransactionsToCsv(
      exportRows,
      `transactions-${buildExportSuffix(dateRange)}.csv`,
    );
    setExportSummary(
      `Exported ${exportRows.length.toLocaleString()} transaction${exportRows.length === 1 ? "" : "s"} (${formatDateRangeLabel(dateRange)}).`,
    );
  }

  function addPending(key: PendingKey) {
    setPendingKeys((prev) => new Set([...prev, key]));
  }
  function removePending(key: PendingKey) {
    setPendingKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  function handleAction(row: InboxRow, action: MatchAction, targetId?: string) {
    const key = makePendingKey(row.key, action);
    addPending(key);
    setActionError(null);
    const callbacks = {
      onSettled: () => removePending(key),
      onError: (error: unknown) =>
        setActionError(
          `Couldn't ${action} this transaction: ${
            error instanceof Error ? error.message : "please try again."
          }`,
        ),
    };
    // Reject / unmatch have no target; leave the id out rather than sending it
    // empty.
    const target = targetId
      ? row.targetKind === "so"
        ? { invoiceId: targetId }
        : { poId: targetId }
      : {};
    if (row.source === "paypal") {
      paypalMutation.mutate(
        {
          paypalPaymentId: row.id,
          action,
          ...(targetId ? { poId: targetId } : {}),
        },
        callbacks,
      );
    } else if (row.source === "mercury") {
      // Incoming credits match to an invoice, outgoing to a PO.
      mercuryMutation.mutate(
        { mercuryPaymentId: row.id, action, ...target },
        callbacks,
      );
    } else {
      // Airwallex: incoming rows match to an invoice, outgoing to a PO.
      airwallexMutation.mutate(
        { airwallexPaymentId: row.id, action, ...target },
        callbacks,
      );
    }
  }

  function openMatchDialog(rowKey: string) {
    setMatchSearchEnabled(true);
    setDetailKey(null);
    setMatchDialogKey(rowKey);
    setSelectedIdForMatch("");
  }

  function closeMatchDialog() {
    setMatchDialogKey(null);
    setSelectedIdForMatch("");
  }

  function openPoSheet(poId: string) {
    setDetailKey(null);
    setSheetPoId(poId);
  }

  function openSoSheet(soId: string) {
    setDetailKey(null);
    setSheetSoId(soId);
  }

  const matchDialogRow = matchDialogKey
    ? (rows.find((r) => r.key === matchDialogKey) ?? null)
    : null;

  function confirmManualMatch() {
    if (!matchDialogRow || !selectedIdForMatch) return;
    handleAction(matchDialogRow, "match", selectedIdForMatch);
    closeMatchDialog();
  }

  const poSearchOptions = useMemo(
    () =>
      allPurchaseOrders.map((po) => {
        const total = getPurchaseOrderTotal({
          buyPrice: po.buyPrice,
          taxCost: po.taxCost,
          totalAmount: po.totalAmount,
        });
        const vendor = po.vendorDisplayName || po.vendorEmail || "?";
        const label = `${po.productName} — ${vendor} — ${formatCurrency({ value: total })} — ${po.id.slice(0, 8)}`;
        return {
          value: po.id,
          label,
          keywords: [po.productName, vendor, po.id],
        };
      }),
    [allPurchaseOrders],
  );

  // Open invoices, labelled with their sales order's customer, for matching an
  // incoming payment.
  const invoiceSearchOptions = useMemo(() => {
    const soById = new Map(allSalesOrders.map((so) => [so.id, so] as const));
    return allOpenInvoices.map((inv) => {
      const so = inv.soId ? soById.get(inv.soId) : undefined;
      const customer = so?.clientDisplayName || so?.clientEmail || "?";
      const product = so?.productName || "Invoice";
      const label = `${product} — ${customer} — ${formatCurrency({ value: inv.amountDue })} — ${inv.id.slice(0, 8)}`;
      return {
        value: inv.id,
        label,
        keywords: [product, customer, inv.id],
      };
    });
  }, [allOpenInvoices, allSalesOrders]);

  const isIncomingMatch = matchDialogRow?.targetKind === "so";
  const matchOptions = isIncomingMatch ? invoiceSearchOptions : poSearchOptions;
  const matchLoading = isIncomingMatch ? allInvoicesLoading : allPosLoading;

  const detailRow = detailKey
    ? (rows.find((r) => r.key === detailKey) ?? null)
    : null;

  // The linked order for a row — a PO for outgoing, a sales order for incoming.
  type LinkedOrder =
    | {
        kind: "po";
        po: PurchaseOrder;
        role: "matched" | "suggested";
        mismatchPct: number;
        label: string;
        sublabel: string;
      }
    | {
        kind: "so";
        so: SalesOrder;
        role: "matched" | "suggested";
        mismatchPct: number;
        label: string;
        sublabel: string;
      };

  function orderFor(row: InboxRow): LinkedOrder | null {
    if (row.targetKind === "so") {
      const matchedId = row.matchStatus === "matched" ? row.matchedSoId : "";
      const suggestedId =
        row.matchStatus === "suggested" ? row.suggestedSoId : "";
      const role = matchedId ? "matched" : suggestedId ? "suggested" : null;
      const soId = matchedId || suggestedId;
      if (!role || !soId) return null;
      const so = soMap.get(soId);
      if (!so) return null;
      return {
        kind: "so",
        so,
        role,
        mismatchPct:
          role === "suggested" ? (row.suggestionMismatchPct ?? 0) : 0,
        label: so.productName,
        sublabel: so.clientDisplayName || so.clientEmail || "",
      };
    }

    if (row.matchStatus === "matched" && row.matchedPoId) {
      const po = poMap.get(row.matchedPoId);
      if (po)
        return {
          kind: "po",
          po,
          role: "matched",
          mismatchPct: 0,
          label: po.productName,
          sublabel: po.vendorDisplayName || po.vendorEmail || "",
        };
    }
    if (row.matchStatus === "suggested" && row.suggestedPoId) {
      const po = poMap.get(row.suggestedPoId);
      if (po)
        return {
          kind: "po",
          po,
          role: "suggested",
          mismatchPct: row.suggestionMismatchPct ?? 0,
          label: po.productName,
          sublabel: po.vendorDisplayName || po.vendorEmail || "",
        };
    }
    return null;
  }

  function openLinkedOrder(linked: LinkedOrder) {
    if (linked.kind === "po") openPoSheet(linked.po.id);
    else openSoSheet(linked.so.id);
  }

  const FilterRow = ({
    label,
    options,
    value,
    onChange,
  }: {
    label: string;
    options: { label: string; value: string }[];
    value: string;
    onChange: (v: string) => void;
  }) => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-20 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      {options.map((opt) => (
        <Button
          key={opt.value}
          variant={value === opt.value ? "default" : "outline"}
          size="sm"
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="container mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>Transactions</CardTitle>
            <CardDescription>
              PayPal, Mercury, and Airwallex Bank transactions pulled in by the
              sync, held as an inbox to be reconciled against orders — outgoing
              against purchase orders, incoming against sales orders. Filter by
              source, direction, date, and keyword, click a row for full
              details, then approve, manually match, or reject each one.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            loading={exporting}
            loadingText="Exporting…"
            disabled={isLoading}
            onClick={handleExportCsv}
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="space-y-2">
            <FilterRow
              label="Source"
              options={SOURCE_OPTIONS}
              value={sourceFilter}
              onChange={(v) => setSourceFilter(v as SourceFilter)}
            />
            <FilterRow
              label="Direction"
              options={DIRECTION_OPTIONS}
              value={directionFilter}
              onChange={(v) => setDirectionFilter(v as DirectionFilter)}
            />
            <FilterRow
              label="Status"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as StatusFilter)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-20 text-xs font-medium text-muted-foreground">
                Date
              </span>
              <Button
                variant={dateRange?.from ? "outline" : "default"}
                size="sm"
                onClick={() => setDateRange(undefined)}
              >
                All time
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDateRange(monthToDateRange())}
              >
                Month to date
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDateRange(yearToDateRange())}
              >
                Year to date
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={dateRange?.from ? "default" : "outline"}
                    size="sm"
                    className="min-w-0 justify-start overflow-hidden text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {dateRange?.from
                        ? formatDateRangeLabel(dateRange)
                        : "Custom range"}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-20 text-xs font-medium text-muted-foreground">
                Keyword
              </span>
              <Input
                type="search"
                placeholder="Counterparty or bank description"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className="h-8 w-full max-w-sm"
              />
            </div>
          </div>

          {/* Query errors (e.g. a Firestore missing-index error carries a
              create-index URL in its message) */}
          {queryErrors.length > 0 && (
            <div className="space-y-2 rounded-md border border-destructive/50 bg-destructive/5 p-3">
              {queryErrors.map(([source, error]) => (
                <div key={source} className="text-xs text-destructive">
                  <span className="font-medium">{source} query failed:</span>{" "}
                  <span className="break-all">{error.message}</span>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                If this is a missing-index error, open the URL in the message to
                create it (or run{" "}
                <code>firebase deploy --only firestore:indexes</code>), then
                reload.
              </p>
            </div>
          )}

          {actionError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-xs text-destructive">
              {actionError}
            </div>
          )}

          {/* Summary counts */}
          {!isLoading && rows.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {statusCounts.suggested > 0 && (
                <span>{statusCounts.suggested} suggested · </span>
              )}
              {statusCounts.unmatched > 0 && (
                <span>{statusCounts.unmatched} unmatched · </span>
              )}
              {statusCounts.matched > 0 && (
                <span>{statusCounts.matched} matched · </span>
              )}
              {statusCounts.rejected > 0 && (
                <span>{statusCounts.rejected} rejected · </span>
              )}
              <span>
                {rows.length} {keyword.trim() ? "matching" : "loaded"}
                {hasNextPage
                  ? keyword.trim()
                    ? " (searching loaded rows only — use Load more to search further)"
                    : " (more available — use Load more)"
                  : ""}{" "}
                · {formatDateRangeLabel(dateRange)}
              </span>
            </p>
          )}

          {exportSummary && (
            <p className="text-xs text-foreground">{exportSummary}</p>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Direction</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Counterparty</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className="py-8 text-center text-muted-foreground"
                  >
                    Loading payments…
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className="py-8 text-center text-muted-foreground"
                  >
                    {keyword.trim()
                      ? `No loaded payments match "${keyword.trim()}"${hasNextPage ? " — use Load more to search further" : ""}.`
                      : "No payments found."}
                  </TableCell>
                </TableRow>
              )}

              {rows.map((row) => {
                const dateStr =
                  formatTimestamp(row.paidAt, "yyyy-MM-dd") ?? "—";
                const linkedOrder = orderFor(row);

                return (
                  <TableRow
                    key={row.key}
                    className="cursor-pointer"
                    onClick={() => setDetailKey(row.key)}
                  >
                    <TableCell>
                      <SourceBadge source={row.source} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {dateStr}
                    </TableCell>
                    <TableCell>
                      <DirectionBadge direction={row.direction} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold">
                      {formatCurrency({ value: row.amount })}
                    </TableCell>
                    <TableCell className="max-w-[220px] text-sm">
                      {row.counterparty ? (
                        <>
                          <span
                            className="block truncate"
                            title={row.counterparty}
                          >
                            {row.counterparty}
                          </span>
                          {row.counterpartySubtitle && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {row.counterpartySubtitle}
                            </span>
                          )}
                        </>
                      ) : (
                        <span
                          className="text-muted-foreground"
                          title={
                            row.description
                              ? `No payer reported by ${SOURCE_LABEL[row.source]} — open the row for the bank description`
                              : undefined
                          }
                        >
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[160px] text-sm">
                      <span
                        className="block truncate text-muted-foreground"
                        title={row.account}
                      >
                        {row.account || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <MatchStatusBadge status={row.matchStatus} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {linkedOrder ? (
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-left underline-offset-2 hover:underline"
                          onClick={() => openLinkedOrder(linkedOrder)}
                        >
                          <span className="block max-w-[180px] truncate">
                            {linkedOrder.label}
                          </span>
                          <span className="block max-w-[180px] truncate text-xs text-muted-foreground">
                            {linkedOrder.sublabel}
                          </span>
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PaymentInboxActions
                        id={row.key}
                        matchStatus={row.matchStatus}
                        suggestedPoId={
                          row.targetKind === "so"
                            ? row.suggestedInvoiceId
                            : row.suggestedPoId
                        }
                        pendingKeys={pendingKeys}
                        onAction={(_, action, targetId) =>
                          handleAction(row, action, targetId)
                        }
                        onOpenMatch={openMatchDialog}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                loading={isFetchingNextPage}
                loadingText="Loading…"
                onClick={loadMore}
              >
                Load more
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment detail sheet */}
      {detailRow &&
        (() => {
          const linkedOrder = orderFor(detailRow);
          const orderNoun =
            detailRow.targetKind === "so" ? "sales order" : "purchase order";
          return (
            <PaymentDetailSheet
              open={detailKey !== null}
              onOpenChange={(open) => {
                if (!open) setDetailKey(null);
              }}
              title={detailRow.title}
              amount={formatCurrency({ value: detailRow.amount })}
              fee={
                detailRow.feeValue > 0
                  ? formatCurrency({ value: detailRow.feeValue })
                  : undefined
              }
              statusBadge={
                <div className="flex items-center gap-2">
                  <SourceBadge source={detailRow.source} />
                  <MatchStatusBadge status={detailRow.matchStatus} />
                </div>
              }
              poSection={
                linkedOrder ? (
                  <div className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">
                      {linkedOrder.role === "matched"
                        ? `Matched ${orderNoun}`
                        : `Suggested ${orderNoun}`}
                      {linkedOrder.role === "suggested" &&
                      linkedOrder.mismatchPct > 0
                        ? ` · ≈ ${(linkedOrder.mismatchPct * 100).toFixed(1)}% off total`
                        : ""}
                    </p>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-left underline-offset-2 hover:underline"
                      onClick={() => openLinkedOrder(linkedOrder)}
                    >
                      {linkedOrder.label}
                      {linkedOrder.sublabel ? ` — ${linkedOrder.sublabel}` : ""}
                    </Button>
                  </div>
                ) : undefined
              }
              rows={detailRow.detailRows}
              actions={
                <PaymentInboxActions
                  id={detailRow.key}
                  matchStatus={detailRow.matchStatus}
                  suggestedPoId={
                    detailRow.targetKind === "so"
                      ? detailRow.suggestedInvoiceId
                      : detailRow.suggestedPoId
                  }
                  pendingKeys={pendingKeys}
                  onAction={(_, action, targetId) =>
                    handleAction(detailRow, action, targetId)
                  }
                  onOpenMatch={openMatchDialog}
                  layout="stacked"
                />
              }
            />
          );
        })()}

      {/* Manual match dialog */}
      <Dialog
        open={matchDialogKey !== null}
        onOpenChange={(open) => {
          if (!open) closeMatchDialog();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isIncomingMatch
                ? "Match to Sales Order (invoice)"
                : "Match to Purchase Order"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {matchLoading && (
              <p className="text-xs text-muted-foreground">
                {isIncomingMatch
                  ? "Loading open invoices…"
                  : "Loading all purchase orders…"}
              </p>
            )}
            <SearchableSelect
              value={selectedIdForMatch}
              onChange={setSelectedIdForMatch}
              options={matchOptions}
              placeholder={
                isIncomingMatch
                  ? "Select an invoice…"
                  : "Select a purchase order…"
              }
              searchPlaceholder={
                isIncomingMatch
                  ? "Search by customer, product, or ID…"
                  : "Search by name, vendor, or ID…"
              }
              emptyText={
                isIncomingMatch
                  ? "No open invoices found."
                  : "No purchase orders found."
              }
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeMatchDialog}>
              Cancel
            </Button>
            <Button disabled={!selectedIdForMatch} onClick={confirmManualMatch}>
              Match
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO detail sheet */}
      <PurchaseOrderSheet
        poId={sheetPoId}
        open={sheetPoId.length > 0}
        onOpenChange={(open) => {
          if (!open) setSheetPoId("");
        }}
      />

      {/* Sales order detail sheet (incoming matches) */}
      <SalesOrderSheet
        soId={sheetSoId}
        open={sheetSoId.length > 0}
        onOpenChange={(open) => {
          if (!open) setSheetSoId("");
        }}
      />
    </div>
  );
}
